/**
 * Stage 4: putting a court paper on the case, and saying which of the four it is.
 *
 * The office already splits a court bundle into **Body** and **Annexures**, and
 * keeps its own file for the reply (the POV document). The app drafts from
 * those whole, so the one thing it has to be told at upload is what each file
 * is — and, for a Body or a file of Annexures, which pleading it belongs to.
 *
 * **A filed document has two doors, and the screen chooses.** A reply this
 * office drafted in the app and then filed is the signed copy of that draft:
 * it is recorded against the draft, never as a second document. A reply that
 * was never drafted here is an outside paper, recorded as the document it is.
 * The officer says which document was filed; whether the app holds a draft of
 * it is a fact the server has, so the door is picked from that and said.
 *
 * Nothing here is refused for what the case holds, and a missing paper never
 * stops a draft: what is expected and not uploaded is named where the draft is
 * started.
 *
 * **A file uploaded before the question was asked is answered where it is
 * listed.** It used to read *Kind not said* with a pointer to another page, so
 * the officer left the document he was drafting to say one word about a file.
 * The list now asks, beside the file, with the same four kinds and the same
 * command the Papers page uses.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api/client'
import type { SourceFileView } from '../api/client'
import {
  COURT_UPLOAD_GUIDE,
  COURT_KINDS,
  PLEADINGS,
  answeredPleading,
  belongsToPleading,
  fileKindWords,
  fileReadingWords,
  fileStillReading,
  pagesWords,
  uploadDescription,
} from '../lib/court'
import type { CourtKind, PleadingKey } from '../lib/court'
import { ErrorEnvelope } from './bits'
import { ANSWERED_PARAWISE } from './pleading'

/** The papers on the case, polled only while a file is still being read. */
/** The office's way of uploading a court case's papers, in four lines. */
export function CourtUploadGuide() {
  return (
    <ul className="small muted court-upload-guide" data-testid="court-upload-guide">
      {COURT_UPLOAD_GUIDE.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  )
}

export function useCasePapers(caseId: string) {
  return useQuery({
    queryKey: ['papers', caseId],
    queryFn: () => api.listSourceFiles(caseId),
    refetchInterval: (query) =>
      (query.state.data?.files ?? []).some(fileStillReading) ? 2500 : false,
  })
}

/** Saying what a file already on the case is: the existing command, with one
 *  of the four court kinds. It files nothing and deletes nothing. */
export const sayPaperKind = (caseId: string, fileId: string, kind: CourtKind) =>
  api.setPaperKind(caseId, fileId, { preflight: false, kind })

/** The question the upload asks, put to a file that was never asked it. */
function SayKind({ caseId, file }: { caseId: string; file: SourceFileView }) {
  const client = useQueryClient()
  const [choice, setChoice] = useState<CourtKind | ''>('')
  const say = useMutation({
    mutationFn: (kind: CourtKind) => sayPaperKind(caseId, file.id, kind),
    onSuccess: () => {
      // What a file is decides what is offered as the pleading and what a
      // draft is written from, so both are asked for again.
      void client.invalidateQueries({ queryKey: ['papers', caseId] })
      void client.invalidateQueries({ queryKey: ['pleading-papers', caseId] })
      void client.invalidateQueries({ queryKey: ['court-preflight', caseId] })
      void client.invalidateQueries({ queryKey: ['record', caseId] })
    },
  })
  return (
    <span className="court-say-kind" data-testid="say-kind">
      <select
        aria-label={`What ${file.filename} is`}
        value={choice}
        onChange={(event) => {
          setChoice(event.target.value as CourtKind | '')
          say.reset()
        }}
        disabled={say.isPending}
      >
        <option value="">Say what this file is…</option>
        {COURT_KINDS.map((option) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="quiet"
        disabled={!choice || say.isPending}
        title={choice ? undefined : 'Choose what the file is first.'}
        onClick={() => choice && say.mutate(choice)}
      >
        {say.isPending ? 'Saving…' : 'Save'}
      </button>
      <ErrorEnvelope error={say.error} />
    </span>
  )
}

export function UploadedFiles({ caseId }: { caseId: string }) {
  const papers = useCasePapers(caseId)
  const files = (papers.data?.files ?? []).filter((file) => !file.removed_at)
  // A file the office marked as a paper of the disciplinary case itself. It
  // has a kind — not one of the four court kinds — so it is not asked again
  // here: marking it a court paper is a change, made on the Papers page.
  const otherwise = (file: SourceFileView) => Boolean(file.paper_kind) && !fileKindWords(file)
  return (
    <section className="court-files" aria-labelledby="court-files-title">
      <h3 id="court-files-title">Files uploaded on this case</h3>
      {papers.isLoading ? <p className="small muted">Reading the list of files…</p> : null}
      {!papers.isLoading && !files.length ? (
        <p className="small muted">No file has been uploaded yet.</p>
      ) : null}
      {files.length ? (
        <ul>
          {files.map((file) => {
            const kind = fileKindWords(file)
            const reading = fileReadingWords(file)
            const said = file.other_description ?? file.document_type ?? ''
            return (
              <li key={file.id}>
                <a
                  className="mono"
                  href={api.sourceFilePath(caseId, file.id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {file.filename}
                </a>
                <span>
                  {kind ??
                    (otherwise(file)
                      ? 'A paper of the case, not a court paper'
                      : 'Kind not said')}
                  {said ? <span className="muted"> · {said}</span> : null}
                  {!file.paper_kind ? <SayKind caseId={caseId} file={file} /> : null}
                </span>
                <span className="small muted">
                  {[pagesWords(file.page_count), reading.words].filter(Boolean).join(' · ')}
                </span>
              </li>
            )
          })}
        </ul>
      ) : null}
      {files.some(otherwise) ? (
        <p className="small muted">
          A paper of the case can be marked as a court paper on the Papers page of the case.
        </p>
      ) : null}
    </section>
  )
}

export function AddCourtPaper({
  caseId,
  documentType,
}: {
  caseId: string
  documentType?: string
}) {
  const client = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [kind, setKind] = useState<CourtKind | ''>('')
  const [pleading, setPleading] = useState<PleadingKey>(answeredPleading(documentType))
  const [filed, setFiled] = useState('')
  const [filedOn, setFiledOn] = useState('')

  // The titles of the documents the department files, off the case's own rail
  // — the pinned process version, never a literal (stop list 6).
  const detail = useQuery({ queryKey: ['case', caseId], queryFn: () => api.getCase(caseId) })
  const filedChoices = (detail.data?.rail ?? []).filter((row) =>
    ANSWERED_PARAWISE.has(row.document_type.toUpperCase()),
  )
  const filedRow = filedChoices.find((row) => row.document_type === filed)
  const filedTitle = filedRow ? (filedRow.short_title ?? filedRow.title) : ''

  // Whether the app holds a draft of the document that was filed. It decides
  // the door, so it is asked rather than assumed.
  const drafts = useQuery({
    queryKey: ['versions', caseId, filed],
    queryFn: () => api.listVersions(caseId, filed),
    enabled: kind === 'filed-document' && Boolean(filed),
  })
  const ownDraft = kind === 'filed-document' ? drafts.data?.[0] : undefined

  const upload = useMutation({
    mutationFn: async () => {
      const chosen = kind as CourtKind
      if (chosen === 'filed-document' && ownDraft) {
        await api.recordSignedCopy(
          caseId,
          filed,
          ownDraft.document_id,
          file!,
          undefined,
          filedOn || undefined,
        )
        return 'filed-own' as const
      }
      await api.uploadSourceFile(caseId, file!, {
        paper_kind: chosen,
        other_description: uploadDescription(chosen, pleading, filedTitle),
        ...(chosen === 'filed-document' ? { document_type: filed } : {}),
        ...(chosen === 'filed-document' && filedOn ? { document_date: filedOn } : {}),
      })
      return 'uploaded' as const
    },
    onSuccess: () => {
      setFile(null)
      void client.invalidateQueries({ queryKey: ['papers', caseId] })
      void client.invalidateQueries({ queryKey: ['pleading-papers', caseId] })
      void client.invalidateQueries({ queryKey: ['court-preflight', caseId] })
      void client.invalidateQueries({ queryKey: ['reply-evidence', caseId] })
      void client.invalidateQueries({ queryKey: ['case', caseId] })
      void client.invalidateQueries({ queryKey: ['finalised', caseId] })
    },
  })

  const checkingDoor = kind === 'filed-document' && Boolean(filed) && drafts.isLoading
  const why = !file
    ? 'Choose the file first.'
    : !kind
      ? 'Say what this file is.'
      : kind === 'filed-document' && !filed
        ? 'Say which document was filed.'
        : checkingDoor
          ? 'Checking whether the app holds a draft of that document…'
          : kind === 'filed-document' && drafts.error
            ? 'Whether the app holds a draft of that document could not be checked, so the copy cannot be recorded in the right place yet. Choose the document again to retry.'
            : ''

  return (
    <section className="d18-add-material" aria-labelledby="add-material-title">
      <h3 id="add-material-title">Add a case document</h3>
      <CourtUploadGuide />
      <div className="court-upload">
        <input
          aria-label="Choose a case document"
          type="file"
          accept=".pdf,.docx"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null)
            upload.reset()
          }}
        />
        <label className="field">
          <span className="label">What is this file?</span>
          <select
            aria-label="What this file is"
            value={kind}
            onChange={(event) => setKind(event.target.value as CourtKind | '')}
            disabled={upload.isPending}
          >
            <option value="">Choose what this file is…</option>
            {COURT_KINDS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {belongsToPleading(kind) ? (
          <label className="field">
            <span className="label">Which pleading does it belong to?</span>
            <select
              aria-label="The pleading this file belongs to"
              value={pleading}
              onChange={(event) => setPleading(event.target.value as PleadingKey)}
              disabled={upload.isPending}
            >
              {PLEADINGS.filter(
                (option) => kind === 'pleading-body' || !('bodyOnly' in option),
              ).map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {kind === 'filed-document' ? (
          <>
            <label className="field">
              <span className="label">Which document was filed?</span>
              <select
                aria-label="The document that was filed"
                value={filed}
                onChange={(event) => setFiled(event.target.value)}
                disabled={upload.isPending}
              >
                <option value="">Choose the document…</option>
                {filedChoices.map((row) => (
                  <option key={row.document_type} value={row.document_type}>
                    {row.short_title ?? row.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="label">
                Date it was filed <span className="small muted">Optional.</span>
              </span>
              <input
                type="date"
                className="mono"
                aria-label="Date it was filed"
                value={filedOn}
                onChange={(event) => setFiledOn(event.target.value)}
                disabled={upload.isPending}
              />
            </label>
            {filed && !drafts.isLoading && !drafts.error ? (
              <p className="small muted">
                {ownDraft
                  ? `The app holds a draft of this document (version ${ownDraft.version}). The filed copy is recorded against it and takes its place when later documents are drafted.`
                  : 'The app holds no draft of this document. The filed copy is recorded on the case as that document.'}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
      {/* Blocked, on screen, with what is missing — never a button that does
          nothing and says nothing. */}
      <div className={why ? 'blocked inline unsettled' : 'row'}>
        <button
          type="button"
          className="quiet"
          disabled={Boolean(why) || upload.isPending}
          onClick={() => upload.mutate()}
        >
          {upload.isPending ? 'Uploading…' : 'Upload document'}
        </button>
        {why ? <span className="why">{why}</span> : null}
      </div>
      {upload.isSuccess ? (
        <p role="status" className="small">
          {upload.data === 'filed-own'
            ? 'The filed copy is recorded against the app’s draft. It is read in the background and used once it has been read.'
            : 'Document added. It is read in the background and used once it has been read.'}
        </p>
      ) : null}
      <ErrorEnvelope error={upload.error} />
    </section>
  )
}
